import React, { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Download, Search, Users } from "lucide-react";
import { bondTrusteesAPI, bondsAPI, formatDate } from "../../services/api";
import LoadingSpinner from "../../components/common/LoadingSpinner";
import { useAuth } from "../../contexts/AuthContext";

const BondHolders = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedBondId, setSelectedBondId] = useState(null);

  // Fetch assigned bonds for the current trustee
  const {
    data: bondsData,
    isPending: bondsLoading,
    error: bondsError,
  } = useQuery({
    queryKey: ["trustee-bonds-all", user?.id],
    queryFn: () => bondTrusteesAPI.getTrusteeBonds(user.id || user._id),
    enabled: !!user,
    select: (response) => {
      // Extract the assignments array from the response
      const assignments = response?.data?.data || response?.data || [];
      // Ensure it's always an array and extract bonds
      const assignmentsArray = Array.isArray(assignments) ? assignments : [];
      return assignmentsArray.map(assignment => assignment.bondId).filter(Boolean);
    },
  });

  const bonds = bondsData || [];

  // Auto-select first bond if none selected and bonds are loaded
  useEffect(() => {
    if (bonds.length > 0 && !selectedBondId) {
      setSelectedBondId(bonds[0]._id);
    }
  }, [bonds, selectedBondId]);

  // Get selected bond details
  const selectedBond = bonds.find((bond) => bond._id === selectedBondId);

  // Fetch holders for the selected bond only
  const {
    data: holdersData,
    isPending: isHoldersLoading,
    error: holdersError,
  } = useQuery({
    queryKey: ["bond-holders", selectedBondId],
    queryFn: () => bondsAPI.getBondHoldersByBondId(selectedBondId),
    enabled: !!selectedBondId,
  });

  const holders = holdersData?.data?.data?.holders || [];

  // Filter holders based on search term
  const filteredHolders = useMemo(() => {
    if (!searchTerm) {
      return holders;
    }

    return holders.filter((holder) => {
      const investor = holder.investorId || {};
      const email = investor.email?.toLowerCase() || "";
      const name =
        investor.personalInfo?.fullName?.toLowerCase() ||
        investor.corporateInfo?.legalEntityName?.toLowerCase() ||
        investor.corporateInfo?.companyName?.toLowerCase() ||
        "";
      const walletAddress = investor.walletAddress?.toLowerCase() || "";
      const search = searchTerm.toLowerCase();

      return (
        email.includes(search) ||
        name.includes(search) ||
        walletAddress.includes(search)
      );
    });
  }, [holders, searchTerm]);

  const isLoading = bondsLoading || isHoldersLoading;

  const exportToCSV = () => {
    const headers = [
      "Investor Name",
      "Email",
      "Wallet Address",
      "Bond Amount",
      "Allocation Date",
    ];
    const rows = filteredHolders.map((holder) => {
      const investor = holder.investorId || {};
      const name =
        investor.personalInfo?.fullName ||
        investor.corporateInfo?.legalEntityName ||
        investor.corporateInfo?.companyName ||
        "N/A";
      return [
        name,
        investor.email || "N/A",
        investor.walletAddress || "N/A",
        holder.tokenAmount?.toString() || "0",
        holder.createdAt ? formatDate(holder.createdAt) : "N/A",
      ];
    });

    const csvContent = [
      headers.join(","),
      ...rows.map((row) => row.map((cell) => `"${cell}"`).join(",")),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    const bondName = selectedBond?.bondName || "bond";
    a.href = url;
    a.download = `${bondName}-holders-${new Date().toISOString().split("T")[0]
      }.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (isLoading) {
    return <LoadingSpinner size="lg" text="Loading bond holders..." />;
  }

  if (bondsError || holdersError) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">Error loading bond holders</p>
          <button
            onClick={() => navigate("/trustee/bonds")}
            className="text-blue-600 hover:text-blue-900"
          >
            Back to Bonds
          </button>
        </div>
      </div>
    );
  }

  // Show message if no assigned bonds
  if (!isLoading && bonds.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="bg-white shadow">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center py-4">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  All Bond Holders
                </h1>
                <p className="text-sm text-gray-500">
                  View all bondholders across your assigned bonds
                </p>
              </div>
              <button
                onClick={() => navigate("/trustee/bonds")}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                Back to Bonds
              </button>
            </div>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
            <Users className="h-12 w-12 mx-auto mb-4 text-gray-400" />
            <p className="text-lg font-medium mb-2">No assigned bonds</p>
            <p className="text-sm">
              You don't have any bonds assigned to you yet.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-gray-900">Bond Holders</h1>
              <p className="text-sm text-gray-500">
                View bondholders for your assigned bonds
              </p>
            </div>
            <div className="flex gap-4">
              <button
                onClick={exportToCSV}
                disabled={!selectedBondId || filteredHolders.length === 0}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Download className="h-4 w-4 mr-2" />
                Export CSV
              </button>
              <button
                onClick={() => navigate("/trustee/bonds")}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                Back to Bonds
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Bond Selection */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Select Bond
          </label>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {bonds.map((bond) => (
              <button
                key={bond._id}
                onClick={() => setSelectedBondId(bond._id)}
                className={`p-4 rounded-lg border-2 text-left transition-all ${selectedBondId === bond._id
                    ? "border-blue-500 bg-blue-50"
                    : "border-gray-200 bg-white hover:border-gray-300"
                  }`}
              >
                <div className="font-semibold text-gray-900">
                  {bond.bondName}
                </div>
                <div className="text-sm text-gray-600 mt-1">
                  {bond.bondNumber}
                </div>
                <div className="text-xs text-gray-500 mt-2">
                  {bond.issuer?.corporateInfo?.companyName ||
                    bond.issuer?.corporateInfo?.legalEntityName ||
                    bond.issuer?.email ||
                    "N/A"}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Search Filter */}
        {selectedBondId && (
          <div className="mb-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name, email, or wallet address..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
        )}

        {/* Stats and Table - Only show when bond is selected */}
        {selectedBondId && (
          <>
            {/* Bond Information Card */}
            <div className="bg-white rounded-lg shadow p-6 mb-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">
                {selectedBond?.bondName}
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                <div>
                  <div className="text-gray-500">Bond Number</div>
                  <div className="font-semibold text-gray-900 mt-1">
                    {selectedBond?.bondNumber}
                  </div>
                </div>
                <div>
                  <div className="text-gray-500">Issuer</div>
                  <div className="font-semibold text-gray-900 mt-1">
                    {selectedBond?.issuer?.corporateInfo?.companyName ||
                      selectedBond?.issuer?.corporateInfo?.legalEntityName ||
                      selectedBond?.issuer?.email ||
                      "N/A"}
                  </div>
                </div>
                <div>
                  <div className="text-gray-500">Total Holders</div>
                  <div className="font-semibold text-gray-900 mt-1">
                    {holders.length}
                  </div>
                </div>
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center">
                  <Users className="h-8 w-8 text-blue-600 mr-4" />
                  <div>
                    <p className="text-sm text-gray-600">Total Holders</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {filteredHolders.length}
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center">
                  <div className="h-8 w-8 bg-green-100 rounded-full flex items-center justify-center mr-4">
                    <span className="text-green-600 font-bold">✓</span>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Active Allocations</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {
                        filteredHolders.filter(
                          (h) => parseFloat(h.tokenAmount || 0) > 0
                        ).length
                      }
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center">
                  <div className="h-8 w-8 bg-purple-100 rounded-full flex items-center justify-center mr-4">
                    <span className="text-purple-600 font-bold">$</span>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Total Tokens</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {filteredHolders
                        .reduce(
                          (sum, h) => sum + parseFloat(h.tokenAmount || 0),
                          0
                        )
                        .toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Holders Table */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
              {filteredHolders.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <Users className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                  <p className="text-lg font-medium mb-2">
                    No bond holders found
                  </p>
                  <p className="text-sm">
                    {searchTerm
                      ? "Try adjusting your search criteria"
                      : "No holders found for this bond"}
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Investor Name
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Email
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Wallet Address
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Bond Amount
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Allocation Date
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredHolders.map((holder, index) => {
                        const investor = holder.investorId || {};
                        const name =
                          investor.personalInfo?.fullName ||
                          investor.corporateInfo?.legalEntityName ||
                          investor.corporateInfo?.companyName ||
                          "N/A";
                        return (
                          <tr
                            key={holder._id || index}
                            className="hover:bg-gray-50"
                          >
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm font-medium text-gray-900">
                                {name}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-500">
                                {investor.email || "N/A"}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-500 font-mono">
                                {investor.walletAddress
                                  ? `${investor.walletAddress.slice(
                                    0,
                                    6
                                  )}...${investor.walletAddress.slice(-4)}`
                                  : "N/A"}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm font-medium text-gray-900">
                                {parseFloat(
                                  holder.tokenAmount || 0
                                ).toLocaleString()}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {holder.createdAt
                                ? formatDate(holder.createdAt)
                                : "N/A"}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default BondHolders;
